import { ReactElement, useContext } from 'react';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { Heading } from '@appsumo/dorado-react';

import { useCheckoutState } from '~/hooks/checkout';
import { GlobalContext } from '~/contexts/global';
import { CartProps } from '~/types/cart';
import { isFreebie } from '~/lib/checkout/util';

import Account from '../Account/Account';
import Billing from '../Billing/Billing';
import CreditsCoupons from '../CreditsCoupons/CreditsCoupons';
import Payment from '../Payment/Payment';
import Summary from '../Summary/Summary';
import Empowered from '~/public/empowered.svg';
import { KlarnaMessage } from '~/components/global/KlarnaMessage';
import { CampaignIncentive } from '~/components/checkout/Summary/CampaignIncentive';

const EmptyCart = dynamic(
  () => import('~/components/checkout/Global/EmptyCart'),
);

const RenderSteps = ({
  cart,
  currentBillingCountry,
  cartHasPlusDeal = false,
  isCreditsCouponsEnabled,
}: {
  cart: CartProps | undefined;
  currentBillingCountry: string;
  cartHasPlusDeal?: boolean;
  isCreditsCouponsEnabled: boolean;
}): ReactElement => {
  if (!cart) return <></>;

  if (!cart.cart_items?.length) {
    return <EmptyCart />;
  }

  if (!isFreebie(cart)) {
    return (
      <>
        <Billing isCreditsCouponsEnabled={isCreditsCouponsEnabled} />
        {!cartHasPlusDeal && (
          <KlarnaMessage
            className="hidden md:block"
            currentBillingCountry={currentBillingCountry}
          />
        )}
        {isCreditsCouponsEnabled && <CreditsCoupons />}
        <Payment isCreditsCouponsEnabled={isCreditsCouponsEnabled} />
      </>
    );
  }

  return (
    <div className="flex items-center bg-[#E9F1FC] px-2 py-4 font-medium">
      <Image
        src={Empowered}
        alt="empowered-icon"
        className="mr-2 h-10 w-10"
        height={40}
        width={40}
      />
      <span>No payment method is needed - this one is on us!</span>
    </div>
  );
};

export function Container({
  cartHasPlusDeal = false,
  isCreditsCouponsEnabled,
}: Readonly<{
  cartHasPlusDeal?: boolean;
  isCreditsCouponsEnabled: boolean;
}>): ReactElement {
  const { cart, currentBillingCountry } = useCheckoutState();
  const {
    campaigns: { showCampaignIncentive },
  } = useContext(GlobalContext);

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 md:gap-x-20">
      <div className="col-span-1 md:col-span-3">
        <Heading.H1 className="mb-6">Checkout</Heading.H1>
        <div className="flex flex-col gap-6">
          <Account />
          <RenderSteps
            cart={cart}
            currentBillingCountry={currentBillingCountry}
            cartHasPlusDeal={cartHasPlusDeal}
            isCreditsCouponsEnabled={isCreditsCouponsEnabled}
          />
        </div>
      </div>
      <div className="col-span-2">
        <Heading.H3 className="mb-4 hidden md:block">Order summary</Heading.H3>
        {showCampaignIncentive && (
          <CampaignIncentive className="mb-4 hidden md:flex" />
        )}
        <Summary isCreditsCouponsEnabled={isCreditsCouponsEnabled} />
      </div>
    </div>
  );
}
